/* eslint-disable @typescript-eslint/no-explicit-any */
import { parseObjects } from '@whistler-ui/k2/helpers';
import { FlexibleObject } from '@whistler-ui/k2/types/common';

import { WS_DEFAULT_SETTINGS } from '../constants';
import { BrandResponse } from '../types';
import { requestWithOptions } from './helper';

const basePath: string =
  import.meta.env.REACT_APP_API_ENDPOINT_WEBSHEET_CONFIG || '/';

export interface WebSheetSettings {
  brandId: string;
  tag: string;
  environment_id: string;
  settings: FlexibleObject;
  timestamp?: string;
  brandSlug?: string;
  websheet_tag?: string;
  configuration_settings: FlexibleObject;
}

export interface WebsheetUsecaseSettingsRequest<T = FlexibleObject> {
  brand_id: string;
  brand_slug?: string;
  configuration_settings: T;
  environment_id: string;
  timestamp?: string;
  websheet_tag: string;
}

interface Localisation {
  locale: string;
  data: FlexibleObject;
}

export const getWebsheetBrands = async (environment_id: string) => {
  const options: RequestInit = { method: 'GET' };
  const path = `${basePath}/${environment_id}/brands`;
  const res = await requestWithOptions(path, options);
  return res as BrandResponse[];
};

export const getWebsheetSettingsByBrandId = async ({
  environmentId,
  brandId,
  tag,
}: {
  environmentId: string;
  brandId: string;
  tag: string;
}) => {
  const options: RequestInit = { method: 'GET' };
  const path = `${basePath}/${environmentId}/brands/${brandId}/websheets/${tag}`;
  const payload = await requestWithOptions(path, options);
  return payload;
};

export const getWebSheetLocalisations = async ({
  environmentId,
  brandId,
  tag,
}: {
  environmentId: string;
  brandId: string;
  tag: string;
}) => {
  const options: RequestInit = { method: 'GET' };
  const path = `${basePath}/${environmentId}/brands/${brandId}/websheets/${tag}/localisations`;
  const response = await requestWithOptions(path, options);
  return parseLocalisationList(response) as Localisation[];
};

export const updateWebSheetSettingsByBrandId = async (
  payload: WebsheetUsecaseSettingsRequest,
) => {
  const options: RequestInit = {
    method: 'PUT',
    body: JSON.stringify(payload),
  };
  const path = `${basePath}/${payload.environment_id}/brands/${payload.brand_id}/websheets/${payload.websheet_tag}`;
  const res = await requestWithOptions(path, options);
  return res;
};

export const createWebSheetSettingsByBrandId = async (
  settings: WebsheetUsecaseSettingsRequest,
) => {
  const { environment_id, brand_id, ...payload } = settings;
  const options: RequestInit = {
    method: 'POST',
    body: JSON.stringify(payload),
  };

  const path = `${basePath}/${environment_id}/brands/${brand_id}/websheets`;
  const res = await requestWithOptions(path, options);
  return res;
};

export const createWebSheetSettings = async (payload: any) => {
  const options: RequestInit = {
    method: 'POST',
    body: mapWebSheetSettingsToPayload(payload),
  };
  const path = `${basePath}/${payload.environment_id}/brands/${payload.brandId}/websheets`;
  const res = await requestWithOptions(path, options);
  return res;
};

export const deleteWebSheetLocalisation = async (payload: {
  environmentId: string;
  brandId: string;
  tag: string;
  locale: string;
}) => {
  const options: RequestInit = { method: 'DELETE' };
  const path = `${basePath}/${payload.environmentId}/brands/${payload.brandId}/websheets/${payload.tag}/localisations/${payload.locale}`;
  const response = await requestWithOptions(path, options);
  return response;
};

export const createWebSheetLocalisations = async (ws: any) => {
  const options: RequestInit = {
    method: 'POST',
    body: JSON.stringify(ws.localisation),
  };
  const path = `${basePath}/${ws.environmentId}/brands/${ws.brandId}/websheets/${ws.tag}/localisations`;
  const payload = await requestWithOptions(path, options);
  return payload;
};

// END REFACTOR
export const getWebSheetSettings = async ({
  environment_id,
  brandId,
  tag,
}: {
  environment_id: string;
  brandId: string;
  tag: string;
}) => {
  const options: RequestInit = { method: 'GET' };
  const path = `${basePath}/${environment_id}/brands/${brandId}/websheets/${tag}`;
  let res: any = null;
  try {
    const payload = await requestWithOptions(path, options);
    res = mapPayloadToWebSheetSettings(payload);
  } catch (error) {
    res = { environment_id, brandId, tag, settings: WS_DEFAULT_SETTINGS[tag] };
  }
  return res;
};

export const updateWebSheetSettings = async (ws: any) => {
  const options: RequestInit = {
    method: 'PUT',
    body: mapWebSheetSettingsToPayload(ws),
  };
  const path = `${basePath}/${ws.environment_id}/brands/${ws.brandId}/websheets/${ws.tag}`;
  const res = await requestWithOptions(path, options);
  return res;
};

export const deleteWebSheetSettings = async (ws: any) => {
  const options: RequestInit = { method: 'DELETE' };
  const path = `${basePath}/${ws.environment_id}/brands/${ws.brandId}/websheets/${ws.tag}`;
  const res = await requestWithOptions(path, options);
  return res;
};

export const updateWebSheetLocalisations = async (ws: any) => {
  const options: RequestInit = {
    method: 'PUT',
    body: JSON.stringify(ws.localisation),
  };
  const path = `${basePath}/${ws.environmentId}/brands/${ws.brandId}/websheets/${ws.tag}/localisations/${ws.localisation.locale}`;
  const payload = await requestWithOptions(path, options);
  return payload;
};

// UPDATED BRANDING SERVICES
export const createWebSheetBrand = async ({
  brand,
}: {
  brand: BrandResponse;
}): Promise<BrandResponse> => {
  const options: RequestInit = {
    method: 'POST',
    body: JSON.stringify(brand),
  };
  const path = `${basePath}/${brand.environment_id}/brands`;
  const res = await requestWithOptions(path, options);
  return res;
};

export const updateWebSheetBrand = async ({
  brand,
}: {
  brand: BrandResponse;
}): Promise<BrandResponse> => {
  const options: RequestInit = {
    method: 'PUT',
    body: JSON.stringify(brand),
  };
  const path = `${basePath}/${brand.environment_id}/brands/${brand.id}`;
  const res = await requestWithOptions(path, options);
  return res;
};

export const deleteWebSheetBrand = async ({
  brand,
}: {
  brand: BrandResponse;
}): Promise<BrandResponse> => {
  const options: RequestInit = { method: 'DELETE' };
  const path = `${basePath}/${brand.environment_id}/brands/${brand.id}`;
  const res = await requestWithOptions(path, options);
  return res;
};

export const setPrimaryWebSheetBrand = async ({
  brand,
}: {
  brand: BrandResponse;
}): Promise<BrandResponse> => {
  const options: RequestInit = {
    body: JSON.stringify({ primary_brand_id: brand.id }),
    method: 'PUT',
  };
  const path = `${basePath}/${brand.environment_id}/config`;
  const res = await requestWithOptions(path, options);
  return res;
};

// HELPERS
const mapPayloadToWebSheetSettings = (ws: any) => {
  return {
    brandId: ws.brand_id,
    tag: ws.websheet_tag,
    environment_id: ws.environment_id,
    settings: ws.configuration_settings,
    timestamp: ws.timestamp,
    brandSlug: ws.brand_slug,
  };
};

const mapWebSheetSettingsToPayload = (ws: any): string => {
  return JSON.stringify({
    timestamp: ws.timestamp,
    brand_id: ws.brandId,
    websheet_tag: ws.tag,
    environment_id: ws.environment_id,
    configuration_settings: ws.settings,
    brand_slug: ws.brandSlug,
  });
};

const parseLocalisationList = (localisations: any[]): Localisation[] => {
  if (localisations.length) {
    localisations = localisations.map(item => {
      item.data = parseObjects(item.data, err => console.error(err));
      return item;
    });
    return localisations;
  }
  return [];
};
